import type { QuickBooksCashPlanningResponse } from "../../../data/ServerApiClient";

/**
 * Total available cash: cash on hand + reserves + UNDRAWN credit.
 *
 * The number Rob Lund manages to, rather than the bank balance. The whole
 * design constraint is in one word: **undrawn**.
 *
 * Adding a credit line's LIMIT to available cash tells an owner they have room
 * they may already have spent, in the one view they consult before deciding
 * whether they can make payroll. So the line is included only when the server
 * resolved `credit_line_undrawn` — which it does only when the drawn balance is
 * known — and otherwise the strip shows the limit as context, explicitly
 * outside the total, and says the draw is unknown. An absent number someone can
 * go and fill in beats a confident wrong one.
 */
export type CfoCashAvailableStripProps = {
  planning?: Pick<
    QuickBooksCashPlanningResponse,
    | "cash_position"
    | "cash_reserve_amount"
    | "credit_line_limit"
    | "credit_line_drawn"
    | "credit_line_drawn_at"
    | "credit_line_undrawn"
  > | null;
  currency: (amount: number) => string;
  /** Considered stale past this many days; the draw is hand-maintained. */
  staleAfterDays?: number;
};

const num = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export default function CfoCashAvailableStrip({
  planning,
  currency,
  staleAfterDays = 45,
}: CfoCashAvailableStripProps) {
  const cash = num(planning?.cash_position?.ending_cash);
  const reserves = num(planning?.cash_reserve_amount);
  const limit = num(planning?.credit_line_limit);
  const drawn = num(planning?.credit_line_drawn);
  const undrawn = num(planning?.credit_line_undrawn);

  // Nothing beyond cash is configured: this strip would restate the table's
  // first cell, so it does not render at all.
  if (cash == null || (reserves == null && limit == null)) return null;

  const parts: { key: string; label: string; amount: number }[] = [
    { key: "cash", label: "Cash on hand", amount: cash },
  ];
  if (reserves != null) parts.push({ key: "reserves", label: "Reserves", amount: reserves });
  if (undrawn != null) parts.push({ key: "undrawn", label: "Undrawn credit", amount: undrawn });

  const total = parts.reduce((sum, part) => sum + part.amount, 0);

  const confirmedAt = planning?.credit_line_drawn_at
    ? new Date(`${planning.credit_line_drawn_at}T00:00:00Z`)
    : null;
  const ageDays =
    confirmedAt && !Number.isNaN(confirmedAt.getTime())
      ? Math.floor((Date.now() - confirmedAt.getTime()) / 86_400_000)
      : null;
  const stale = ageDays != null && ageDays > staleAfterDays;

  return (
    <div
      data-testid="cash-available-strip"
      style={{
        display: "flex",
        alignItems: "baseline",
        gap: 14,
        flexWrap: "wrap",
        padding: "8px 12px",
        marginBottom: 10,
        border: "1px solid var(--line, #e5e0d8)",
        borderRadius: 8,
      }}
    >
      <span style={{ fontSize: 18, fontWeight: 700 }} data-testid="cash-available-total">
        {currency(total)}
      </span>
      <span style={{ fontSize: 12, color: "var(--muted, #868c9a)" }}>total available cash</span>
      <span style={{ fontSize: 12, color: "var(--muted, #868c9a)", marginLeft: "auto" }}>
        {parts.map((part) => `${part.label} ${currency(part.amount)}`).join("  ·  ")}
      </span>
      {undrawn == null && limit != null ? (
        <span
          data-testid="cash-available-undrawn-unknown"
          style={{ fontSize: 12, color: "var(--muted, #868c9a)", width: "100%" }}
        >
          Credit line of {currency(limit)} is not counted above — record what is
          drawn on it to include the room that is actually left.
        </span>
      ) : null}
      {stale && drawn != null ? (
        <span
          data-testid="cash-available-drawn-stale"
          style={{ fontSize: 12, color: "var(--muted, #868c9a)", width: "100%" }}
        >
          Drawn balance last confirmed {ageDays} days ago.
        </span>
      ) : null}
    </div>
  );
}
